<template>
    <div class="register-container">
        <div class="register-form">
            <div class="logo-container">
                <img src="@/assets/images/sellhub-logo.png" alt="SellHub Logo" class="logo" />
            </div>

            <h1>Créer un compte</h1>

            <div v-if="error" class="error-message">
                {{ error }}
            </div>

            <!-- Étape 1: Informations personnelles -->
            <form v-if="step === 1" @submit.prevent="goToStep2">
                <div class="form-group">
                    <label for="name">Nom complet</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </span>
                        <input id="name" v-model="form.name" type="text" placeholder="Votre nom complet"
                            :class="{ 'input-error': validationErrors.name }" required />
                    </div>
                    <span v-if="validationErrors.name" class="field-error">{{ validationErrors.name[0] }}</span>
                </div>

                <div class="form-group">
                    <label for="email">Email</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z">
                                </path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                        </span>
                        <input id="email" v-model="form.email" type="email" placeholder="Votre adresse email"
                            :class="{ 'input-error': validationErrors.email }" required />
                    </div>
                    <span v-if="validationErrors.email" class="field-error">{{ validationErrors.email[0] }}</span>
                </div>

                <div class="form-group">
                    <label for="company_name">Nom de l'entreprise</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                        </span>
                        <input id="company_name" v-model="form.company_name" type="text"
                            placeholder="Nom de votre entreprise"
                            :class="{ 'input-error': validationErrors.company_name }" required />
                    </div>
                    <span v-if="validationErrors.company_name" class="field-error">{{ validationErrors.company_name[0]
                        }}</span>
                </div>

                <div class="form-group">
                    <label for="business_type">Type d'activité</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                            </svg>
                        </span>
                        <select id="business_type" v-model="form.business_type"
                            :class="{ 'input-error': validationErrors.business_type }">
                            <option value="">Sélectionnez un type d'activité</option>
                            <option value="retail">Commerce de détail</option>
                            <option value="restaurant">Restaurant / Fast-food</option>
                            <option value="service">Service</option>
                            <option value="other">Autre</option>
                        </select>
                    </div>
                    <span v-if="validationErrors.business_type" class="field-error">{{ validationErrors.business_type[0]
                        }}</span>
                </div>

                <button type="submit" class="register-button">
                    Suivant
                </button>
            </form>

            <!-- Étape 2: Mot de passe -->
            <form v-if="step === 2" @submit.prevent="handleRegister">
                <div class="steps-indicator">
                    <div class="step-progress">
                        <div class="step-progress-bar" style="width: 100%;"></div>
                    </div>
                    <p class="step-text">Étape 2 sur 2</p>
                </div>

                <div class="form-group">
                    <label for="password">Mot de passe</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </span>
                        <input id="password" v-model="form.password" :type="passwordVisible ? 'text' : 'password'"
                            placeholder="Choisissez un mot de passe sécurisé"
                            :class="{ 'input-error': validationErrors.password }" required />
                        <span class="toggle-password" @click="togglePasswordVisibility">
                            <svg v-if="!passwordVisible" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" stroke-linejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path
                                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24">
                                </path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                        </span>
                    </div>
                    <span v-if="validationErrors.password" class="field-error">{{ validationErrors.password[0] }}</span>
                </div>

                <div class="form-group">
                    <label for="password_confirmation">Confirmation du mot de passe</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </span>
                        <input id="password_confirmation" v-model="form.password_confirmation"
                            :type="passwordVisible ? 'text' : 'password'" placeholder="Confirmez votre mot de passe"
                            required />
                    </div>
                </div>

                <div class="form-actions">
                    <button type="button" class="back-button" @click="step = 1">
                        Retour
                    </button>
                    <button type="submit" class="register-button" :disabled="isLoading">
                        <span v-if="isLoading" class="loader"></span>
                        <span>{{ isLoading ? 'Création du compte...' : 'Créer mon compte' }}</span>
                    </button>
                </div>
            </form>

            <div class="login-link">
                <p>Vous avez déjà un compte ? <router-link :to="{ name: 'login' }">Se connecter</router-link></p>
            </div>
        </div>

        <footer class="footer">
            <p>© {{ new Date().getFullYear() }} SellHub. Tous droits réservés.</p>
            <p>Powered by <span class="powered-by">SellHub</span></p>
        </footer>
    </div>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore, useToastStore } from '@/store/modules'

export default defineComponent({
    name: 'RegisterView',

    setup() {
        const authStore = useAuthStore()
        const toastStore = useToastStore()
        const router = useRouter()
        const step = ref(1)
        const passwordVisible = ref(false)

        const form = reactive({
            name: '',
            email: '',
            company_name: '',
            business_type: '',
            password: '',
            password_confirmation: ''
        })

        const isLoading = ref(false)
        const error = ref(null)
        const validationErrors = ref({})

        const togglePasswordVisibility = () => {
            passwordVisible.value = !passwordVisible.value
        }

        const goToStep2 = () => {
            validationErrors.value = {}
            error.value = null

            // Vérifications basiques côté client
            if (!form.name) {
                validationErrors.value.name = ['Le nom est requis']
                return
            }

            if (!form.email) {
                validationErrors.value.email = ['L\'email est requis']
                return
            }

            if (!form.company_name) {
                validationErrors.value.company_name = ['Le nom de l\'entreprise est requis']
                return
            }

            if (!form.business_type) {
                validationErrors.value.business_type = ['Le type d\'activité est requis']
                return
            }

            // Passer à l'étape suivante
            step.value = 2
        }

        const handleRegister = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}

            // Vérifier que les mots de passe correspondent
            if (form.password !== form.password_confirmation) {
                validationErrors.value.password = ['Les mots de passe ne correspondent pas']
                isLoading.value = false
                return
            }

            try {
                await authStore.register(form)

                toastStore.addToast({
                    type: 'success',
                    message: 'Inscription réussie ! Veuillez vérifier votre email pour activer votre compte.',
                    duration: 6000
                })
                
                router.push({ name: 'login' })
            } catch (err) {
                console.error('Registration error:', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'

                    // Retourner à l'étape 1 si les erreurs concernent les informations personnelles
                    if (validationErrors.value.name || validationErrors.value.email ||
                        validationErrors.value.company_name || validationErrors.value.business_type) {
                        step.value = 1
                    }
                }
                else {
                    error.value = err.response?.data?.message || "Une erreur est survenue lors de l'inscription"
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            form,
            isLoading,
            error,
            validationErrors,
            step,
            passwordVisible,
            togglePasswordVisibility,
            goToStep2,
            handleRegister
        }
    }
})
</script>

<style scoped>
.register-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: #f8f9fa;
    font-family: 'Poppins', 'sans-serif';
}

.register-form {
    width: 100%;
    max-width: 600px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    padding: 30px;
    margin: 20px;
}

.logo-container {
    text-align: center;
    margin-bottom: 30px;
}

.logo {
    height: 50px;
    width: auto;
}

h1 {
    color: #1e3a8a;
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 30px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    font-size: 16px;
    color: #1e3a8a;
    margin-bottom: 8px;
    font-weight: 500;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    color: #0A5C6B;
}

.toggle-password {
    position: absolute;
    right: 14px;
    cursor: pointer;
    color: #0A5C6B;
}

input,
select {
    width: 100%;
    padding: 16px 45px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 16px;
    background-color: #f8fafc;
    color: #1e293b;
    transition: border-color 0.3s, box-shadow 0.3s;
}

select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230A5C6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
}

input:focus,
select:focus {
    outline: none;
    border-color: #3FDA4D;
    box-shadow: 0 0 0 3px rgba(63, 218, 77, 0.2);
}

input::placeholder,
select::placeholder {
    color: #a0aec0;
}

.input-error {
    border-color: #ef4444 !important;
}

.field-error {
    display: block;
    color: #ef4444;
    font-size: 14px;
    margin-top: 5px;
}

.form-actions {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.back-button {
    flex: 1;
    padding: 16px;
    background-color: white;
    color: #0A5C6B;
    border: 1px solid #0A5C6B;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
}

.back-button:hover {
    background-color: #f1f5f9;
}

.register-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 16px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    margin-bottom: 20px;
}

.register-button:hover {
    background-color: #3FDA4D;
}

.register-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.steps-indicator {
    margin-bottom: 20px;
}

.step-progress {
    height: 8px;
    background-color: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.step-progress-bar {
    height: 100%;
    background-color: #3FDA4D;
    transition: width 0.3s ease;
}

.step-text {
    text-align: right;
    font-size: 14px;
    color: #64748b;
}

.error-message {
    background-color: #fee2e2;
    color: #b91c1c;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 14px;
}

.login-link {
    text-align: center;
    color: #475569;
    font-size: 14px;
}

.login-link a {
    color: #0A5C6B;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s;
}

.login-link a:hover {
    color: #3FDA4D;
    text-decoration: underline;
}

.footer {
    text-align: center;
    color: #64748b;
    font-size: 12px;
    margin-top: 20px;
    padding: 20px;
}

.powered-by {
    color: #3FDA4D;
    font-weight: 600;
}

.loader {
    width: 20px;
    height: 20px;
    border: 2px solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    margin-right: 10px;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 480px) {
    .register-form {
        padding: 20px;
        margin: 10px;
    }

    h1 {
        font-size: 24px;
    }

    input,
    select {
        padding: 14px 40px;
    }

    .register-button,
    .back-button {
        padding: 14px;
    }
}
</style>